import React from 'react';

const LoanQuoteStep2 = ({
        loanQuotation,
        onSubmitApplication,
        onDeclineApplication,
        isSubmitting,
        disableSubmission,
        formType
    }) => {

    const getProductHeading = () => {
        if (formType === 'vat_loan') return "Your VAT Loan Quotation";
        if (formType === 'car_loan') return "Your Car Finance Quotation";
        if (formType === 'business_loan') return "Your Business Loan Quotation";
        return "Your Asset Finance Quotation";
    };

    // Calculate total repayable if not directly provided
    const monthlyNum = parseFloat(String(loanQuotation.regularRepayment || '0').replace(/,/g, ''));
    const termNum = parseInt(loanQuotation.term || 0, 10);
    const balloonNum = parseFloat(String(loanQuotation.balloonPayment || '0').replace(/,/g, ''));
    const computedTotal = (monthlyNum * termNum) + balloonNum;
    const displayTotal = loanQuotation.totalAmountPayable || computedTotal.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return (
        <div className="lqc-quotation-card">
            <div className="lqc-quote-header">
                <h3>{getProductHeading()}</h3>
                <p className="lqc-quote-subtitle">Based on the details and preferences provided</p>
            </div>

            <div className="lqc-quote-body">
                {/* Hero Repayment Box */}
                <div className="lqc-hero-repayment">
                    <span className="lqc-hero-label">Monthly Repayment:</span>
                    <div className="lqc-hero-amount">
                        £{loanQuotation.regularRepayment} <span className="lqc-hero-period">/ mo</span>
                    </div>
                    {loanQuotation.balloonPayment && balloonNum > 0 && (
                        <div className="lqc-balloon-tag">
                            + Final payment of £{loanQuotation.balloonPayment} due at term end
                        </div>
                    )}
                </div>

                {/* 4-Item / 6-Item Metrics Grid */}
                <div className="lqc-breakdown-grid">
                    <div className="lqc-metric-item">
                        <div className="lqc-metric-icon">
                            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" />
                                <path d="M14.5 9c-.5-.7-1.3-1-2.5-1H9.5v8h2.5c1.4 0 2.5-.5 2.5-1.5 0-.8-.6-1.3-1.5-1.5 1.2-.3 2-1 2-2.2 0-.8-.3-1.4-.5-1.8z" />
                            </svg>
                        </div>
                        <div className="lqc-metric-content">
                            <span className="lqc-metric-title">
                                {formType === 'car_loan' ? 'Amount Financed:' : 'Amount Borrowed:'}
                            </span>
                            <span className="lqc-metric-value">£{loanQuotation.loanAmount}</span>
                        </div>
                    </div>

                    <div className="lqc-metric-item">
                        <div className="lqc-metric-icon">
                            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                        </div>
                        <div className="lqc-metric-content">
                            <span className="lqc-metric-title">Term:</span>
                            <span className="lqc-metric-value">{loanQuotation.term} Months</span>
                        </div>
                    </div>

                    <div className="lqc-metric-item">
                        <div className="lqc-metric-icon">
                            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <line x1="19" y1="5" x2="5" y2="19" />
                                <circle cx="6.5" cy="6.5" r="2.5" />
                                <circle cx="17.5" cy="17.5" r="2.5" />
                            </svg>
                        </div>
                        <div className="lqc-metric-content">
                            <span className="lqc-metric-title">{formType === 'car_loan' ? 'APR:' : formType === 'vat_loan' ? 'Interest Rate:' : 'Flat Rate:'}</span>
                            <span className="lqc-metric-value">{loanQuotation.rate}%{formType === 'car_loan' ? ' APR' : ''}</span>
                        </div>
                    </div>

                    <div className="lqc-metric-item">
                        <div className="lqc-metric-icon">
                            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <rect x="2" y="6" width="20" height="12" rx="2" />
                                <circle cx="12" cy="12" r="2" />
                                <path d="M6 12h.01M18 12h.01" />
                            </svg>
                        </div>
                        <div className="lqc-metric-content">
                            <span className="lqc-metric-title">Total Amount Payable:</span>
                            <span className="lqc-metric-value">£{displayTotal}</span>
                        </div>
                    </div>

                    {formType === 'car_loan' && loanQuotation.vehiclePrice && (
                        <>
                            <div className="lqc-metric-item">
                                <div className="lqc-metric-icon">
                                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <path d="M5 17h14v-5l-2-4H7L5 12v5z" />
                                        <circle cx="7.5" cy="17.5" r="1.5" />
                                        <circle cx="16.5" cy="17.5" r="1.5" />
                                    </svg>
                                </div>
                                <div className="lqc-metric-content">
                                    <span className="lqc-metric-title">Vehicle Price:</span>
                                    <span className="lqc-metric-value">£{loanQuotation.vehiclePrice}</span>
                                </div>
                            </div>

                            <div className="lqc-metric-item">
                                <div className="lqc-metric-icon">
                                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                                    </svg>
                                </div>
                                <div className="lqc-metric-content">
                                    <span className="lqc-metric-title">Deposit Paid:</span>
                                    <span className="lqc-metric-value">£{loanQuotation.deposit || '0'}</span>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Email Confirmation Notice */}
                <div className="lqc-email-notice">
                    <div className="lqc-email-notice-icon">
                        <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2zm13 2.383-4.708 2.825L15 11.105V5.383zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741zM1 11.105l4.708-2.897L1 5.383v5.722z"/>
                        </svg>
                    </div>
                    <div>
                        A copy of this quote and formal disclosure documents have been dispatched to your email address.
                    </div>
                </div>

                {/* Action Buttons */}
                <form onSubmit={onSubmitApplication} className="lqc-quote-actions">
                    {disableSubmission ? (
                        <div className="d-flex justify-content-center">
                            <button type="button" className="btn btn-primary px-4 py-2" onClick={onDeclineApplication}>
                                Calculate Another Quote
                            </button>
                        </div>
                    ) : (
                        <div className="lqc-action-buttons-row">
                            <button
                                type="button"
                                className="btn btn-outline-secondary lqc-adjust-btn"
                                onClick={onDeclineApplication}
                            >
                                Adjust Details
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary lqc-apply-btn"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>Submitting application...</>
                                ) : (
                                    <>
                                        Apply Now <span className="ms-1">→</span>
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </form>

                <div className="lqc-trust-footer">
                    <span>🔒 No obligation</span>
                    <span>•</span>
                    <span>⚡ Quick 24h approval</span>
                    <span>•</span>
                    <span>🤝 Dedicated specialist</span>
                </div>
            </div>
        </div>
    );
};

export default LoanQuoteStep2;
